import { Component, inject, Input, OnInit, signal } from "@angular/core";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { OrganizationUserService } from "@bitwarden/common/abstractions/organization-user/organization-user.service";
import { OrganizationService } from "@bitwarden/common/abstractions/organization/organization.service.abstraction";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";

import { buildMembersLabel, loadMembersByCollection } from "../team-members.utils";

interface ShareTeamRow {
  collectionId: string;
  organizationId: string;
  name: string;
  initial: string;
  membersLabel: string;
  checked: boolean;
}

/**
 * "Share with" panel content for the account-sharing slide-out (opened via
 * `DialogFactory.shareAccount`). Lets the user pick one or more teams (collections) to share
 * the account into — the actual fork/share network calls happen in the caller via `onShare`,
 * this component only resolves which teams exist and who's already on them.
 *
 * The confirm button itself is NOT rendered here: `DialogWrapperComponent`'s slide-out footer
 * slot is a static template owned by whichever component opened the dialog (see
 * AccountDetailsComponent's `#shareFooter`), so this exposes `shareButtonOptions` — built once,
 * `onClick` reads `rows` live — via `onButtonOptionsChange` for that footer to render.
 */
@Component({
  selector: "app-share-account",
  templateUrl: "./share-account.component.html",
})
export class ShareAccountComponent implements OnInit {
  @Input() onShare: (collectionIds: string[]) => void;
  @Input() onButtonOptionsChange: (options: ActionButton) => void;

  private apiService = inject(ApiService);
  private collectionService = inject(CollectionService);
  private i18nService = inject(I18nService);
  private logService = inject(LogService);
  private organizationService = inject(OrganizationService);
  private organizationUserService = inject(OrganizationUserService);
  private platformUtilsService = inject(PlatformUtilsService);

  protected loading = signal(true);
  protected loadFailed = signal(false);
  protected rows: ShareTeamRow[] = [];

  // Always the same enabled/teal look, per design — sharing with nothing checked is just a
  // harmless no-op (see `share()`) rather than a disabled/greyed-out state.
  protected readonly shareButtonOptions: ActionButton = new ActionButton({
    text: this.i18nService.t("shareAccount"),
    isEnabled: true,
    onClick: () => this.share(),
    testId: "share-account-confirm",
    fullClass:
      "tw-h-12 tw-w-full tw-rounded-2xl tw-border-none tw-bg-color-action-primary tw-text-white",
  });

  async ngOnInit() {
    this.onButtonOptionsChange?.(this.shareButtonOptions);
    await this.load();
  }

  protected async load() {
    this.loading.set(true);
    this.loadFailed.set(false);
    try {
      const allCollections = await this.collectionService.getAllDecrypted();
      const shareableCollections = allCollections.filter((c) => !c.readOnly);
      const membersByCollection = await loadMembersByCollection(
        this.organizationUserService,
        this.apiService,
        this.organizationService,
        shareableCollections,
        this.logService,
      );

      this.rows = shareableCollections.map((c) => ({
        collectionId: c.id,
        organizationId: c.organizationId,
        name: c.name,
        initial: c.name?.charAt(0).toUpperCase() ?? "",
        membersLabel: buildMembersLabel(this.i18nService, membersByCollection.get(c.id) ?? []),
        checked: false,
      }));
    } catch (e) {
      this.logService.error(e instanceof Error ? (e.stack ?? e.message) : JSON.stringify(e));
      this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
      this.loadFailed.set(true);
      this.rows = [];
    } finally {
      this.loading.set(false);
    }
  }

  protected toggleRow(row: ShareTeamRow) {
    row.checked = !row.checked;
  }

  private share() {
    const collectionIds = this.rows.filter((r) => r.checked).map((r) => r.collectionId);
    if (collectionIds.length === 0) {
      return;
    }
    this.onShare(collectionIds);
  }
}
